import { Matrix } from '../../../platform/geom/Matrix';
import { Path } from '../../../platform/geom/Path';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { Spinner } from './Spinner';
import { SpinnerItem } from './SpinnerItem';

export type SpinnerSegmentDims = {
    ref: string,
    outline: Path,
    field: Point[],
};

const computeSegmentDims = (item: SpinnerItem, disk: Rect, spin: Rect, index: number, total: number): SpinnerSegmentDims => {
    /*
              a
            /
           /
        o /____ u
          \
           \
            \ b

        n = angle
    */

    const o = Point.zero;
    const u = Point.right.multiply((disk.width / 2));
    const n = Math.min(2 * Math.PI / total, Math.PI);
    const a = u.rotate(n / -2);
    const b = u.rotate(n / 2);

    const mt = Matrix.rotate(2 * Math.PI / total * index);

    const outline = Path.builder()
        .moveTo(o)
        .lineTo(a)
        .arcTo(b, [u.length(), u.length(), 0, 1])
        .lineTo(o)
        .build();

    const field =
        (total <= 3 ? new Rect(spin.width / 2, spin.height / -2, u.multiply(.6).length(), spin.height) : null) ??
        Rect.fromPoints([
            a.multiply(.2).add(u.multiply(.1)),
            b.multiply(.2).add(u.multiply(.1)),
            a.multiply(.2).add(u.multiply(.6)),
            b.multiply(.2).add(u.multiply(.6)),
        ]);

    return {
        ref: item.id,
        outline: mt.apply(outline),
        field: mt.apply(field),
    };
};

const transformSegmentDims = (dims: SpinnerSegmentDims[], matrix: Matrix): SpinnerSegmentDims[] =>
    dims.map(x => ({
        ref: x.ref,
        outline: matrix.apply(x.outline),
        field: matrix.apply(x.field),
    }));

export const SpinnerLayout = (object: Spinner) => {

    const base = new Rect(0, 0, object.width, object.height);
    const inner = base.inflate(-10);

    const size = Math.min(base.width, base.height) - 40;
    const disk = Rect.fromCenter(base.center(), size / 2);

    const arm = Rect.fromCenter(
        disk.pointAt(1, .5).add([-10, 0]),
        new Point(size * .2, size * .05),
    );

    const spin = Rect.fromCenter(disk.center(), Math.round(disk.width / 10 * .1) * 10);

    const baseSegments = object
        .polarize(SpinnerItem)
        .filter(x => !x.hidden)
        .map((x, i, arr) => computeSegmentDims(x, disk, spin, i, arr.length));

    const segments = {
        initial: transformSegmentDims(baseSegments, Matrix.translate(disk.center())),
        applied: transformSegmentDims(baseSegments, Matrix.translate(disk.center()).rotate(object.value)),
    };

    const bs = new Point(size / 6, size / 6 / 2);
    const reset = new Rect(inner.left, inner.bottom - bs.h, bs.w, bs.h);
    const remove = new Rect(inner.right - bs.w, inner.bottom - bs.h, bs.w, bs.h);

    return { base, inner, disk, segments, arm, spin, reset, remove };
};

export type SpinnerLayout = ReturnType<typeof SpinnerLayout>;
